'use client';

import { useMemo, useState } from 'react';
import {
  addMatrices,
  determinant,
  formatCell,
  inverseMatrix,
  makeEmptyGrid,
  multiplyMatrices,
  parseGrid,
  resizeGrid,
  subtractMatrices,
  transposeMatrix,
  type Matrix,
} from '@/lib/browser-calc-tools-extra';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

type BinaryOp = 'add' | 'subtract' | 'multiply';
type UnaryOp = 'transpose' | 'determinant' | 'inverse';
type OpResult = { matrix?: Matrix; scalar?: number; error?: string };

const MIN_DIM = 1;
const MAX_DIM = 6;

const SAMPLE_A: string[][] = [
  ['2', '1'],
  ['1', '3'],
];
const SAMPLE_B: string[][] = [
  ['4', '0'],
  ['1', '2'],
];

function clampDim(value: number): number {
  if (!Number.isFinite(value)) {
    return MIN_DIM;
  }
  return Math.min(MAX_DIM, Math.max(MIN_DIM, Math.round(value)));
}

const cellStyle: React.CSSProperties = {
  width: '100%',
  minWidth: '3.5rem',
  textAlign: 'center',
  padding: '0.4rem 0.35rem',
};

function MatrixGridInput({
  grid,
  onChange,
  label,
}: {
  grid: string[][];
  onChange: (row: number, col: number, value: string) => void;
  label: string;
}) {
  return (
    <div
      aria-label={label}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${grid[0]?.length ?? 1}, minmax(3.5rem, 1fr))`,
        gap: '0.4rem',
      }}
    >
      {grid.map((row, r) =>
        row.map((value, c) => (
          <input
            aria-label={`${label} row ${r + 1} column ${c + 1}`}
            inputMode="decimal"
            key={`${r}-${c}`}
            onChange={(event) => onChange(r, c, event.target.value)}
            style={cellStyle}
            type="text"
            value={value}
          />
        )),
      )}
    </div>
  );
}

function ResultGrid({ matrix }: { matrix: Matrix }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${matrix[0]?.length ?? 1}, minmax(3.5rem, 1fr))`,
        gap: '0.4rem',
      }}
    >
      {matrix.map((row, r) =>
        row.map((value, c) => (
          <div
            key={`${r}-${c}`}
            style={{
              padding: '0.5rem 0.35rem',
              textAlign: 'center',
              borderRadius: '0.5rem',
              background: 'rgba(120, 120, 160, 0.12)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatCell(value)}
          </div>
        )),
      )}
    </div>
  );
}

function matrixToText(matrix: Matrix): string {
  return matrix.map((row) => row.map((value) => formatCell(value)).join('\t')).join('\n');
}

export default function BrowserMatrixCalculator() {
  const [rowsA, setRowsA] = useState(2);
  const [colsA, setColsA] = useState(2);
  const [rowsB, setRowsB] = useState(2);
  const [colsB, setColsB] = useState(2);
  const [gridA, setGridA] = useState<string[][]>(SAMPLE_A);
  const [gridB, setGridB] = useState<string[][]>(SAMPLE_B);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  function resizeA(nextRows: number, nextCols: number) {
    const r = clampDim(nextRows);
    const c = clampDim(nextCols);
    setRowsA(r);
    setColsA(c);
    setGridA((prev) => resizeGrid(prev, r, c));
  }

  function resizeB(nextRows: number, nextCols: number) {
    const r = clampDim(nextRows);
    const c = clampDim(nextCols);
    setRowsB(r);
    setColsB(c);
    setGridB((prev) => resizeGrid(prev, r, c));
  }

  function setCellA(row: number, col: number, value: string) {
    setGridA((prev) =>
      prev.map((r, ri) => (ri === row ? r.map((v, ci) => (ci === col ? value : v)) : r)),
    );
  }

  function setCellB(row: number, col: number, value: string) {
    setGridB((prev) =>
      prev.map((r, ri) => (ri === row ? r.map((v, ci) => (ci === col ? value : v)) : r)),
    );
  }

  const binaryResult = useMemo(() => {
    return (op: BinaryOp): OpResult => {
      const a = parseGrid(gridA);
      if (!a.ok) {
        return { error: a.error };
      }
      const b = parseGrid(gridB);
      if (!b.ok) {
        return { error: b.error };
      }
      const result =
        op === 'add'
          ? addMatrices(a.matrix, b.matrix)
          : op === 'subtract'
            ? subtractMatrices(a.matrix, b.matrix)
            : multiplyMatrices(a.matrix, b.matrix);
      return result.ok ? { matrix: result.matrix } : { error: result.error };
    };
  }, [gridA, gridB]);

  const unaryResult = useMemo(() => {
    return (which: 'A' | 'B', op: UnaryOp): OpResult => {
      const parsed = parseGrid(which === 'A' ? gridA : gridB);
      if (!parsed.ok) {
        return { error: parsed.error };
      }
      if (op === 'transpose') {
        const r = transposeMatrix(parsed.matrix);
        return r.ok ? { matrix: r.matrix } : { error: r.error };
      }
      if (op === 'determinant') {
        const r = determinant(parsed.matrix);
        return r.ok ? { scalar: r.value } : { error: r.error };
      }
      const r = inverseMatrix(parsed.matrix);
      return r.ok ? { matrix: r.matrix } : { error: r.error };
    };
  }, [gridA, gridB]);

  const [activeOp, setActiveOp] = useState<
    { kind: 'binary'; op: BinaryOp } | { kind: 'unary'; which: 'A' | 'B'; op: UnaryOp }
  >({ kind: 'binary', op: 'add' });

  const output = useMemo(() => {
    if (activeOp.kind === 'binary') {
      return binaryResult(activeOp.op);
    }
    return unaryResult(activeOp.which, activeOp.op);
  }, [activeOp, binaryResult, unaryResult]);

  async function handleCopy() {
    if (output.error) {
      return;
    }
    try {
      const text =
        typeof output.scalar === 'number'
          ? formatCell(output.scalar)
          : output.matrix
            ? matrixToText(output.matrix)
            : '';
      await copyText(text);
      setCopyMessage('Result copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the result.');
    }
  }

  function loadSample() {
    setRowsA(2);
    setColsA(2);
    setRowsB(2);
    setColsB(2);
    setGridA(SAMPLE_A);
    setGridB(SAMPLE_B);
    setActiveOp({ kind: 'binary', op: 'add' });
    setCopyMessage(null);
  }

  function clearAll() {
    setGridA(makeEmptyGrid(rowsA, colsA));
    setGridB(makeEmptyGrid(rowsB, colsB));
    setCopyMessage(null);
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Matrix Calculator</span>
        <h2 className="tool-live-title">Add, multiply, transpose, and invert matrices</h2>
        <p className="tool-live-subtitle">
          Enter two matrices, adjust the rows and columns, then run an operation. Everything is
          computed live in your browser and nothing is uploaded.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <div className="tool-live-field tool-live-field-full">
              <span>Matrix A size</span>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  aria-label="Matrix A rows"
                  max={MAX_DIM}
                  min={MIN_DIM}
                  onChange={(event) => resizeA(Number(event.target.value), colsA)}
                  style={{ width: '4.5rem' }}
                  type="number"
                  value={rowsA}
                />
                <span aria-hidden="true">×</span>
                <input
                  aria-label="Matrix A columns"
                  max={MAX_DIM}
                  min={MIN_DIM}
                  onChange={(event) => resizeA(rowsA, Number(event.target.value))}
                  style={{ width: '4.5rem' }}
                  type="number"
                  value={colsA}
                />
              </div>
            </div>
            <div className="tool-live-field tool-live-field-full">
              <span>Matrix A</span>
              <MatrixGridInput grid={gridA} label="Matrix A" onChange={setCellA} />
            </div>

            <div className="tool-live-field tool-live-field-full">
              <span>Matrix B size</span>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  aria-label="Matrix B rows"
                  max={MAX_DIM}
                  min={MIN_DIM}
                  onChange={(event) => resizeB(Number(event.target.value), colsB)}
                  style={{ width: '4.5rem' }}
                  type="number"
                  value={rowsB}
                />
                <span aria-hidden="true">×</span>
                <input
                  aria-label="Matrix B columns"
                  max={MAX_DIM}
                  min={MIN_DIM}
                  onChange={(event) => resizeB(rowsB, Number(event.target.value))}
                  style={{ width: '4.5rem' }}
                  type="number"
                  value={colsB}
                />
              </div>
            </div>
            <div className="tool-live-field tool-live-field-full">
              <span>Matrix B</span>
              <MatrixGridInput grid={gridB} label="Matrix B" onChange={setCellB} />
            </div>
          </div>

          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              onClick={() => setActiveOp({ kind: 'binary', op: 'add' })}
              type="button"
            >
              A + B
            </button>
            <button
              className="button button-secondary"
              onClick={() => setActiveOp({ kind: 'binary', op: 'subtract' })}
              type="button"
            >
              A − B
            </button>
            <button
              className="button button-secondary"
              onClick={() => setActiveOp({ kind: 'binary', op: 'multiply' })}
              type="button"
            >
              A × B
            </button>
            <button
              className="button button-secondary"
              onClick={() => setActiveOp({ kind: 'unary', which: 'A', op: 'transpose' })}
              type="button"
            >
              Transpose A
            </button>
            <button
              className="button button-secondary"
              onClick={() => setActiveOp({ kind: 'unary', which: 'A', op: 'determinant' })}
              type="button"
            >
              det(A)
            </button>
            <button
              className="button button-secondary"
              onClick={() => setActiveOp({ kind: 'unary', which: 'A', op: 'inverse' })}
              type="button"
            >
              Inverse A
            </button>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={loadSample} type="button">
              Load sample
            </button>
            <button className="button button-secondary" onClick={clearAll} type="button">
              Clear
            </button>
          </div>
        </section>

        <section className="tool-live-preview">
          <div className="tool-live-field tool-live-field-full">
            <span>Result</span>
            {output.error ? (
              <p className="status-message status-error">{output.error}</p>
            ) : typeof output.scalar === 'number' ? (
              <div
                style={{
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  background: 'rgba(120, 120, 160, 0.12)',
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: '1.25rem',
                }}
              >
                {formatCell(output.scalar)}
              </div>
            ) : output.matrix ? (
              <ResultGrid matrix={output.matrix} />
            ) : null}
          </div>
          {!output.error ? (
            <div className="tool-live-actions">
              <button className="button button-secondary" onClick={handleCopy} type="button">
                Copy result
              </button>
            </div>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
          <p className="tool-live-panel-note">
            Values are rounded to four decimals for display. Matrices up to 6 × 6 are supported.
          </p>
        </section>
      </div>
    </div>
  );
}
